import { useState } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Tab, { tabClasses } from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import FixedBudget from './FixedBudget';
import FixedImpressions from './FixedImpressions';

import { usePlatform } from '@/features/users';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function ForecastingTabPanel(props: Readonly<TabPanelProps>) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`forecasting-tabpanel-${index.toString()}`}
      aria-labelledby={`forecasting-tab-${index.toString()}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `forecasting-tab-${index.toString()}`,
    'aria-controls': `forecasting-tabpanel-${index.toString()}`,
  };
}

const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: '1.25',
  [`&.${tabClasses.selected}`]: {
    color: theme.palette.primary.dark,
    fontWeight: 500,
  },
}));

const ForecastingTabs = {
  fixedBudget: 0,
  fixedImpressions: 1,
};

const Planning = () => {
  const platform = usePlatform();
  const isDV360 = platform === 'DV360';

  const [tabIndex, setTabIndex] = useState(0);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newTabIndex: number
  ) => {
    setTabIndex(newTabIndex);
  };

  return (
    <Grid container>
      <Grid size={6}>
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: (theme) => theme.customShadows.sm,
          }}
        >
          <Box p={3}>
            <Typography>Planning</Typography>
            <Typography variant='body2' color='text.secondary' pt={0.5} pb={3}>
              {tabIndex === ForecastingTabs.fixedBudget
                ? `Adjust ${isDV360 ? 'insertion order' : 'campaign'} budget and CPM to see expected impressions`
                : 'Enter your impressions goal and adjust CPM to see required budget'}
            </Typography>

            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              aria-label='forecasting tabs'
              sx={{ borderBottom: 1, borderColor: 'divider' }}
              slotProps={{
                indicator: {
                  sx: {
                    backgroundColor: 'primary.dark',
                  },
                },
              }}
            >
              <StyledTab
                label='Fixed Budget'
                {...a11yProps(ForecastingTabs.fixedBudget)}
              />
              <StyledTab
                label='Fixed Impressions'
                {...a11yProps(ForecastingTabs.fixedImpressions)}
              />
            </Tabs>
          </Box>

          <ForecastingTabPanel
            value={tabIndex}
            index={ForecastingTabs.fixedBudget}
          >
            <FixedBudget />
          </ForecastingTabPanel>

          <ForecastingTabPanel
            value={tabIndex}
            index={ForecastingTabs.fixedImpressions}
          >
            <FixedImpressions />
          </ForecastingTabPanel>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default Planning;
